<script setup lang="ts">
import Lightbox from 'vue-easy-lightbox'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination } from 'swiper/modules'
import lockScroll from '~/utils/lockScroll'
import { useStoreConfig } from '~/store/storeConfig'

const emit = defineEmits(['handle', 'updated'])
const { isMobile } = useStoreConfig()
const scrollPosition = ref(0)

const controlledSwiper = ref(null)
const setControlledSwiper = (swiper: any) => {
  controlledSwiper.value = swiper
}
const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  images: {
    type: Array as () => string[],
    required: false,
    default: null,
  },
  itemName: {
    type: String,
    required: false,
    default: '',
  },
})

const placeholderImgSrc = '/product-placeholder.png'

const image = useImage()
const visible = ref<Boolean>(props.visible)
const activeImage = ref(0)
const gallery = computed(() => (props.images?.length ? props.images : [placeholderImgSrc]))
const swiperModules = [Pagination]

const optimizedImages = computed(() =>
  gallery.value.map((img) =>
    image(img, {
      format: 'webp',
      quality: 95,
      height: 1000,
    })
  )
)

watch(
  () => props.visible,
  (isVisible) => {
    if (isVisible) {
      scrollPosition.value = window?.scrollY ?? 0
    }

    visible.value = isVisible
    lockScroll(isVisible, scrollPosition.value)

    if (!isVisible) {
      activeImage.value = 0
    }
  },
  {
    immediate: true,
  }
)

function handle(value: boolean) {
  emit('handle', value)
}

const onArrowClick = (index: number) => {
  activeImage.value = index
  controlledSwiper.value?.slideTo(index)
}

const slidesPerView = computed(() => {
  if (isMobile) {
    return gallery.value.length > 2 ? 3 : gallery.value.length
  }

  return gallery.value.length > 4 ? 5 : gallery.value.length
})

const swiperWidth = computed(() => {
  if (isMobile) {
    return 'auto'
  }

  return `${slidesPerView.value * 150}px`
})
</script>

<template>
  <div class="modal-component-body">
    <template v-if="visible">
      <Lightbox
        v-if="images?.length"
        :visible="visible"
        :imgs="optimizedImages"
        :index="activeImage"
        @hide="handle(false)"
        @on-next-click="(_, newIndex) => onArrowClick(newIndex)"
        @on-prev-click="(_, newIndex) => onArrowClick(newIndex)" />
      <div v-if="images?.length > 1" class="lightbox-carousel fixed bottom-12 left-0 w-full h-auto z-[9999] m-auto">
        <ClientOnly>
          <Swiper
            :slides-per-view="slidesPerView"
            :space-between="isMobile ? 0 : 5"
            :modules="swiperModules"
            :pagination="{ dynamicBullets: true }"
            :watch-slides-progress="true"
            :prevent-clicks-propagation="false"
            :threshold="5"
            :initial-slide="activeImage"
            :slide-to-clicked-slide="true"
            :class="swiperWidth"
            :style="{
              maxWidth: swiperWidth,
            }"
            @swiper="setControlledSwiper">
            <SwiperSlide v-for="(thumbnail, index) in gallery" :key="index">
              <div
                class="slide-item flex items-center justify-center"
                :class="{ active: index === activeImage }"
                @click="activeImage = index">
                <NuxtImg format="webp" :src="thumbnail" quality="90" class="object-contain" />
              </div>
            </SwiperSlide>
          </Swiper>
        </ClientOnly>
      </div>
    </template>
  </div>
</template>

<style lang="postcss" scoped>
:deep(.vel-img-wrapper) {
  img {
    min-width: 250px;
    max-height: 50vh;
  }
}

.slide-item {
  margin: auto;
  width: 5rem !important;
  height: 5rem !important;
  border: 1px solid theme('colors.neutral5');
  border-radius: 0.25rem;
  cursor: pointer;
  background: theme('colors.white');
  padding: 0.5rem;

  &.active {
    border-color: theme('colors.brand.primary');
    border-width: 2px;
  }

  img {
    height: 60px !important;
  }
}

.swiper {
  padding-bottom: 1.5rem;
}

:deep(.swiper-pagination) {
  bottom: 0;
}

@media screen and (min-width: 1025px) {
  .slide-item {
    width: 8rem !important;
    height: 8rem !important;
    padding: 1rem;

    img {
      height: 100px !important;
    }
  }
}
</style>
